/**
 * useServiceTypes Hook
 * 
 * Provides dynamic service types from the backend API with caching.
 * Also manages user's last selected service type preference.
 */

import { useState, useEffect, useCallback, useRef } from "react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Module-level cache to share across components
let serviceTypesCache = null;
let cacheTimestamp = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export function useServiceTypes() {
  const [serviceTypes, setServiceTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastSelectedId, setLastSelectedId] = useState(null);
  const [lastSelectedSlug, setLastSelectedSlug] = useState(null);
  const preferenceLoaded = useRef(false);

  // Fetch service types
  const fetchServiceTypes = useCallback(async (forceRefresh = false) => {
    const now = Date.now();
    
    // Use cache if valid and not forcing refresh
    if (!forceRefresh && serviceTypesCache && (now - cacheTimestamp) < CACHE_DURATION) {
      setServiceTypes(serviceTypesCache);
      setLoading(false);
      return serviceTypesCache;
    }

    try {
      const response = await fetch(`${API}/service-types`, {
        credentials: "include",
      });
      
      if (!response.ok) {
        throw new Error("Failed to fetch service types");
      }
      
      const data = await response.json();
      const types = data.service_types || [];
      
      // Update cache
      serviceTypesCache = types;
      cacheTimestamp = now;
      
      setServiceTypes(types);
      setError(null);
      return types;
    } catch (err) {
      console.error("Error fetching service types:", err);
      setError(err.message);
      // Return fallback if cache exists
      if (serviceTypesCache) {
        setServiceTypes(serviceTypesCache);
        return serviceTypesCache;
      }
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch user's preference
  const fetchUserPreference = useCallback(async () => {
    if (preferenceLoaded.current) return;
    
    try {
      const response = await fetch(`${API}/service-types/user-preference`, {
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        setLastSelectedId(data.last_job_service_type_id);
        setLastSelectedSlug(data.service_type_slug);
        preferenceLoaded.current = true;
      }
    } catch (err) {
      console.error("Error fetching service type preference:", err);
    }
  }, []);

  // Update user's preference
  const updateUserPreference = useCallback(async (serviceTypeId) => {
    if (!serviceTypeId) return;
    
    try {
      const response = await fetch(`${API}/service-types/user-preference`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ last_job_service_type_id: serviceTypeId }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setLastSelectedId(data.last_job_service_type_id);
        // Find and set the slug
        const type = serviceTypes.find(t => t.service_type_id === serviceTypeId);
        if (type) {
          setLastSelectedSlug(type.slug);
        }
      }
    } catch (err) {
      console.error("Error updating service type preference:", err);
    }
  }, [serviceTypes]);

  // Get service type ID by slug (for backwards compatibility with existing data)
  const getIdBySlug = useCallback((slug) => {
    if (!slug) return null;
    const normalized = slug.toLowerCase().replace(/[- ]/g, "_");
    const found = serviceTypes.find(t => 
      t.slug === normalized || 
      t.name.toLowerCase().replace(/[- ]/g, "_") === normalized
    );
    return found?.service_type_id || null;
  }, [serviceTypes]);

  // Get slug by service type ID
  const getSlugById = useCallback((id) => {
    if (!id) return null;
    const found = serviceTypes.find(t => t.service_type_id === id);
    return found?.slug || null;
  }, [serviceTypes]);

  // Get display name by slug
  const getNameBySlug = useCallback((slug) => {
    if (!slug) return null;
    const normalized = slug.toLowerCase().replace(/[- ]/g, "_");
    const found = serviceTypes.find(t => 
      t.slug === normalized || 
      t.name.toLowerCase().replace(/[- ]/g, "_") === normalized
    );
    return found?.name || slug;
  }, [serviceTypes]);

  // Get the default service type value for a new job
  // Returns slug (for backwards compatibility with existing job data storage)
  const getDefaultServiceType = useCallback(() => {
    // First, try user's last selected
    if (lastSelectedSlug) {
      // Verify it's still active
      const isActive = serviceTypes.some(t => t.slug === lastSelectedSlug);
      if (isActive) {
        return lastSelectedSlug;
      }
    }
    
    // Fall back to first available service type
    if (serviceTypes.length > 0) {
      return serviceTypes[0].slug;
    }
    
    // Ultimate fallback
    return "general";
  }, [lastSelectedSlug, serviceTypes]);

  // Initial load
  useEffect(() => {
    fetchServiceTypes();
    fetchUserPreference();
  }, [fetchServiceTypes, fetchUserPreference]);

  return {
    // Data
    serviceTypes,
    loading,
    error,
    lastSelectedId,
    lastSelectedSlug,
    
    // Actions
    fetchServiceTypes,
    updateUserPreference,
    
    // Helpers
    getIdBySlug,
    getSlugById,
    getNameBySlug,
    getDefaultServiceType,
    
    // Convenience - formatted options for Select component
    options: serviceTypes.map(t => ({
      value: t.slug,
      label: t.name,
      id: t.service_type_id,
    })),
  };
}

/**
 * Clear the service types cache (useful after admin updates)
 */
export function clearServiceTypesCache() {
  serviceTypesCache = null;
  cacheTimestamp = 0;
}

export default useServiceTypes;
